import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import buildParams from "../../../../Utils/buildParams";
import { useGetList } from "../../../../hooks/useGetList";
import { useParams } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  Skeleton,
  IconButton,
  Tooltip,
  Alert,
  TablePagination,
} from "@mui/material";
import {
  ArrowDownward,
  ArrowUpward,
  SwapHoriz,
  History,
  Inventory,
  Category,
} from "@mui/icons-material";
import { format } from "date-fns";
import formatDate from "../../../../Utils/formatDate";
import {
  setLimit,
  setPage,
} from "../../../../redux/unitConvertionHistory/unitConvertionHistorySlice";

const UnitConvertionHistory = () => {
  const { id } = useParams();
  const filter = useSelector((state) => state.unitConvertionHistory);
  const { refresh } = useSelector((state) => state.refresh);
  const { selectedBranch } = useSelector((state) => state.filterItemUnit);
  const param = buildParams(filter);

  const {
    data: unitConvertionHistory,
    loading,
    refetch,
  } = useGetList(
    `convert-unit/history?relatedItem=${id}&relatedBranch=${selectedBranch}`,
    selectedBranch ? true : false,
    Object.keys(param).length > 0 ? param : undefined,
  );

  const dispatch = useDispatch();

  const handleChangePage = (e, page) => {
    dispatch(setPage(page + 1));
  };
  const handleChangeRowsPerPage = (e) => {
    dispatch(setLimit(e.target.value));
    dispatch(setPage(1));
  };

  useEffect(() => {
    if (selectedBranch) {
      refetch();
    }
  }, [refresh, selectedBranch]);

  const SkeletonRow = () => (
    <TableRow>
      <TableCell>
        <Skeleton variant="text" />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" />
      </TableCell>
      <TableCell>
        <Skeleton variant="text" />
      </TableCell>
    </TableRow>
  );

  const EmptyState = () => (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      py={8}
    >
      <History sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
      <Typography variant="h6" color="text.secondary" gutterBottom>
        No Conversion History
      </Typography>
      <Typography variant="body2" color="text.secondary">
        There are no unit conversion records for this item yet.
      </Typography>
    </Box>
  );

  if (loading) {
    return (
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <Box p={3}>
          <Typography variant="h6" gutterBottom>
            Unit Conversion History
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell>From Unit</TableCell>
                <TableCell>To Unit</TableCell>
                <TableCell>Conversion</TableCell>
                <TableCell>Quantity Change</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...Array(5)].map((_, index) => (
                <SkeletonRow key={index} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  if (!unitConvertionHistory?.data?.length) {
    return (
      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <Box p={3}>
          <Typography variant="h6" gutterBottom>
            Unit Conversion History
          </Typography>
        </Box>
        <EmptyState />
      </Paper>
    );
  }

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <Box p={3}>
        <Typography
          variant="h6"
          gutterBottom
          display="flex"
          alignItems="center"
          gap={1}
        >
          <History />
          Unit Conversion History
        </Typography>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>Item</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>From Unit</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>To Unit</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Conversion</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Quantity Change</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {unitConvertionHistory?.data?.map((record) => (
              <TableRow
                key={record._id}
                hover
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Inventory sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {record.relatedItem?.medicineItemName || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>

                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {record.relatedFromUnit?.name || "N/A"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Qty: {record.fromUnitQtyBefore} →{" "}
                      {record.fromUnitQtyAfter}
                    </Typography>
                  </Box>
                </TableCell>

                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {record.relatedToUnit?.name || "N/A"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Qty: {record.toUnitQtyBefore} → {record.toUnitQtyAfter}
                    </Typography>
                  </Box>
                </TableCell>

                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Tooltip title="Conversion Ratio">
                      <SwapHoriz sx={{ fontSize: 16, color: "primary.main" }} />
                    </Tooltip>
                    <Typography variant="body2">
                      {record.relatedUnitRelation?.fromUnitQty || 1} :{" "}
                      {record.relatedUnitRelation?.toUnitQty || 1}
                    </Typography>
                  </Box>
                </TableCell>

                <TableCell>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <ArrowDownward
                      sx={{
                        fontSize: 14,
                        color: "error.main",
                      }}
                    />
                    <Typography variant="body2" color="error.main">
                      -{record.reducedFromUnitQty}
                    </Typography>
                    <ArrowUpward
                      sx={{
                        fontSize: 14,
                        color: "success.main",
                        ml: 1,
                      }}
                    />
                    <Typography variant="body2" color="success.main">
                      +{record.increasedToUnitQty}
                    </Typography>
                  </Box>
                </TableCell>

                <TableCell>
                  <Chip
                    label={record.isRelationInverted ? "Inverted" : "Normal"}
                    size="small"
                    color={record.isRelationInverted ? "warning" : "success"}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                    {formatDate(record.createdAt)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <div sx={{ display: "flex", justifyContent: "space-between" }}>
        <div>
          {unitConvertionHistory.data.length > 0 && (
            <Box p={2} bgcolor="grey.50">
              <Typography variant="caption" color="text.secondary">
                Showing {unitConvertionHistory.data.length} conversion records
              </Typography>
            </Box>
          )}
        </div>

        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={unitConvertionHistory?.pagination?.total || 0}
          rowsPerPage={filter.limit}
          page={filter.page - 1}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",

            "& .MuiTablePagination-toolbar": {
              display: "flex",
              alignItems: "center",
              minHeight: "52px",
            },

            "& .MuiTablePagination-selectLabel": {
              margin: 0,
              display: "flex",
              alignItems: "center",
            },

            "& .MuiTablePagination-displayedRows": {
              margin: 0,
              display: "flex",
              alignItems: "center",
            },

            "& .MuiTablePagination-actions": {
              display: "flex",
              alignItems: "center",
            },
          }}
        />
      </div>
    </Paper>
  );
};

export default UnitConvertionHistory;
